import React, { useEffect, useState } from "react";
import { Button, Table, Popconfirm, message, Empty, Tooltip } from "antd";
import {
  RetweetOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { translate } from "../../locale";
import UserServices from "../../services/UserServices";
import moment from "moment";
import { TableHelper } from "../../helpers/TableHelper";

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const PendingInvitationsTable = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record, index) => (
        <div className="flex flex-row space-x-4 justify-center items-center">
          <Popconfirm
            title={translate("¿Estás seguro de reenviar la invitación?")}
            okText={translate("Si")}
            cancelText={translate("No")}
            onConfirm={() => {
              handleResendInvitation(
                record.email,
                record.subscription,
                record.rol
              );
            }}
          >
            <Tooltip title={translate("Reenviar invitación")}>
              <Button
                type="text"
                icon={<RetweetOutlined />}
                size="small"
                className="border-0"
              />
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title={translate("¿Estás seguro de eliminar el registro?")}
            okText={translate("Si")}
            cancelText={translate("No")}
            onConfirm={() => {
              handleDelete(record.id);
            }}
          >
            <Tooltip title={translate("Borrar invitación")}>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                className="border-0"
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
      width: 100,
    },
    {
      title: translate("Suscripción"),
      dataIndex: "subscription_name",
      key: "subscription_name",
      sorter: { multiple: 1 },
    },
    {
      title: translate("Email"),
      dataIndex: "email",
      key: "email",
      sorter: { multiple: 2 },
    },
    {
      title: "Estado",
      dataIndex: "expire_date",
      key: "expire_date",
      render: (_, record) => {
        const diff = moment(record.expire_date).diff(moment(), "days");
        if (diff < 1) {
          return (
            <div className="text-custom_darkred">{translate("Expirado")}</div>
          );
        }

        return (
          <>
            <div className="text-custom_darkgreen leading-none">
              {translate("Pendiente")}
            </div>
            <div className="text-xs text-custom_darkgray leading-none">
              {translate("(expira en {0} días)", diff)}
            </div>
          </>
        );
      },
      sorter: { multiple: 3 },
    },
    {
      title: translate("Invitado por"),
      dataIndex: "full_name",
      key: "full_name",
      sorter: { multiple: 4 },
    },
  ];

  useEffect(() => {
    loadData();
  }, [JSON.stringify(tableParams)]);

  const loadData = async () => {
    let data = {
      page_size: tableParams.pagination.pageSize,
      page_num: tableParams.pagination.current,
    };
    const sorter = TableHelper.sorter(tableParams);
    if (sorter !== null) {
      data = { ...data, order_by: sorter };
    }
    setLoading(true);
    try {
      const res = await UserServices.getMyInvites(data);
      if (res.status >= 200 && res.status < 300 && res.data.data.length > 0) {
        setData(
          res.data.data.map((i) => ({
            id: i.id,
            email: i.email,
            expire_date: i.expire_date,
            subscription_name: i.subscription.name,
            full_name: i.emisor.full_name,
            rol: i.rol,
            subscription: i.subscription.id,
          }))
        );
        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleResendInvitation = async (email, subscription, rol) => {
    setLoading(true);
    try {
      const data = {
        email: email,
        type: "email",
        app: "eira",
        subscription: subscription,
        rol: rol,
      };

      const res = await UserServices.sendInvitation(data);
      if (res) {
        if (res.data.message === "Mail Sent") {
          message.success(
            translate("La {0} se envió exitosamente.", translate("invitación"))
          );
        } else {
          message.error(res.data.message);
        }
        setLoading(false);
      } else {
        message.error(translate("No se pudieron guardar sus datos."));
      }
    } catch (e) {
      console.log(e);
      message.error(translate("La invitación no pudo ser enviada."));
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    console.log("delete user ", id);
    setLoading(true);
    try {
      const res = await UserServices.deleteContactInvitation(id);
      if (res.status >= 200 && res.status < 300) {
        message.success(
          translate("La invitación ha sido eliminada exitsamente.")
        );
        loadData();
      } else {
        message.error(translate("La invitación no pudo ser eliminada."));
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      message.error(translate("La invitación no pudo ser eliminada."));
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  return (
    <Table
      rowKey={(i) => {
        return i.id.toString();
      }}
      dataSource={data}
      columns={columns}
      loading={loading && { indicator: antIcon, className: "text-custom_blue" }}
      locale={{
        emptyText: (
          <Empty description={translate("Servicio aun no habilitado")} />
        ),
        sortTitle: translate("Ordenar"),
        triggerDesc: translate("Ordenar (descendente)"),
        triggerAsc: translate("Ordenar (ascendente)"),
        cancelSort: translate("Cancelar orden"),
      }}
      pagination={{ ...tableParams.pagination, total: total }}
      onChange={handleTableChange}
      scroll={{ x: 300 }}
    />
  );
};

export default PendingInvitationsTable;
